import { useState, useEffect, useCallback } from 'react'
import supabase from '../supabase/supabase'
import { sendInviteEmail } from '../services/emailService'

export const useUsers = () => {
  const [users, setUsers] = useState([])
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('full_name')
      if (error) throw error
      setUsers(data)
    } catch (err) {
      console.error('Error fetching users:', err)
    }
  }, [])

  const fetchInvites = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('invites')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
      if (error) throw error
      setInvites(data || [])
    } catch (err) {
      console.error('Error fetching invites:', err)
    }
  }, [])

  const refetch = useCallback(async () => {
    setLoading(true)
    await Promise.all([fetchUsers(), fetchInvites()])
    setLoading(false)
  }, [fetchUsers, fetchInvites])

  const deleteInvite = async (inviteId) => {
    try {
      console.log('Attempting to delete invite:', inviteId);
      
      const { error, count } = await supabase
        .from('invites')
        .delete({ count: 'exact' })
        .eq('id', inviteId);
      
      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      console.log('Delete successful, rows affected:', count);
      
      // Update local state immediately
      setInvites(prev => prev.filter(inv => inv.id !== inviteId));
      
      return { success: true };
    } catch (err) {
      console.error('Delete invite caught error:', err);
      return { success: false, error: err.message || 'Failed to delete invitation' };
    }
  }

  const updateUserRole = async (userId, newRole) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
      if (error) throw error
      await fetchUsers()
      return { success: true }
    } catch (err) {
      return { success: false, error: err.message }
    }
  }

  const deleteUser = async (userId) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId)
      if (error) throw error
      await fetchUsers()
      return { success: true }
    } catch (err) {
      return { success: false, error: err.message }
    }
  }

  const resendInvite = async (inviteId, email, fullName, role) => {
    try {
      const result = await sendInviteEmail(email, fullName, role)
      if (!result.success) throw new Error(result.error)
      return { success: true }
    } catch (err) {
      return { success: false, error: err.message }
    }
  }

  useEffect(() => {
    refetch()
  }, [refetch])

  return { 
    users, 
    invites, 
    loading, 
    error, 
    updateUserRole, 
    deleteUser, 
    resendInvite, 
    deleteInvite, 
    refetch 
  }
}